// LG-only parental management. None of these operations grant playback,
// subscription, rental, territory, or account-age authorization.
export const PARENTAL_PIN_PATTERN = /^[0-9]{6}$/;
export const PARENTAL_CODE_PATTERN = /^[A-F0-9]{24}$/;
const PURPOSES = new Set(['setup', 'reset']);

export function normalizeParentalCode(value) {
  return String(value || '').trim().toUpperCase().replace(/[ -]/g, '');
}

export function validateParentalPin(pin, confirmation) {
  if (!PARENTAL_PIN_PATTERN.test(String(pin))) throw new Error('Choose a PIN with exactly six digits.');
  if (pin !== confirmation) throw new Error('The PINs do not match. Enter them again.');
}

function parentalError(code, status) {
  const messages = {
    account_signin_required: 'Sign in again with the WatchVIM account used on your LG TV.',
    invalid_challenge: 'This request could not be verified for your account. Check the code on your LG TV.',
    challenge_used: 'This TV request has already been used. Check your LG TV before starting a new request.',
    reauth_used: 'This verification has already been used. Start a new TV request and sign in again.',
    service_unavailable: 'Parental controls are temporarily unavailable. Check your LG TV before trying again.',
    reauth_required: 'Sign in again with your account password, then approve this request.',
    mfa_required: 'This account requires multi-factor verification. PIN changes are blocked until supported account verification is completed.',
    challenge_expired: 'This TV request has expired. Start a new PIN request on your LG TV.',
    expired: 'This TV request has expired. Start a new PIN request on your LG TV.',
    invalid_code: 'This request could not be verified for your account. Check the code on your LG TV.',
    forbidden: 'This request could not be verified for your account. Use the account signed in on your LG TV.',
    rate_limited: 'Too many attempts. Wait before trying again.',
    unavailable: 'Parental controls are temporarily unavailable. Your PIN has not been changed.',
  };
  const error = new Error(messages[code] || (status === 429
    ? messages.rate_limited
    : 'The request could not be completed. Check your LG TV and sign in again.'));
  error.code = code || 'request_failed';
  error.status = status;
  return error;
}

export function createTvParentalService({ fetchImpl = (...args) => fetch(...args), now = () => Date.now() } = {}) {
  async function request(accessToken, body) {
    if (!accessToken) throw parentalError('reauth_required', 401);
    if (!PARENTAL_CODE_PATTERN.test(body.code)) throw new Error('Enter the 24-character code shown on your LG TV.');
    let response;
    try {
      response = await fetchImpl('/api/tv/parental/owner', {
        method: 'POST',
        credentials: 'omit',
        cache: 'no-store',
        redirect: 'error',
        referrerPolicy: 'no-referrer',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify(body),
      });
    } catch {
      throw new Error('The result could not be confirmed. Check your LG TV before starting a new request.');
    }
    const payload = await response.json().catch(() => null);
    if (!response.ok || payload?.ok === false) {
      throw parentalError(payload?.code || payload?.error, response.status);
    }
    if (!payload || typeof payload !== 'object') throw parentalError('invalid_response', response.status);
    return payload;
  }

  return {
    async info({ accessToken, code }) {
      const payload = await request(accessToken, { action: 'info', code: normalizeParentalCode(code) });
      const expiresAt = Date.parse(payload.expiresAt);
      if (!PURPOSES.has(payload.purpose) || !Number.isFinite(expiresAt)) throw parentalError('invalid_response', 502);
      if (expiresAt <= now()) throw parentalError('challenge_expired', 410);
      return { purpose: payload.purpose, expiresAt };
    },
    async complete({ accessToken, code, purpose, pin, confirmation, confirmed }) {
      if (!PURPOSES.has(purpose)) throw new Error('Verify this TV request again before continuing.');
      if (confirmed !== true) throw new Error('Confirm that you approve this specific PIN change.');
      validateParentalPin(pin, confirmation);
      const payload = await request(accessToken, {
        action: 'complete', code: normalizeParentalCode(code), purpose, pin,
      });
      if (payload.configured !== true) throw parentalError('invalid_response', 502);
      return { configured: true };
    },
  };
}

export const tvParentalService = createTvParentalService();
